/**
 * Derives Story List tab, folder id, and sidebar selection from the current URL.
 *
 * Replaces `selectStoryListTabFromRoute`, `selectStoryListFolderIdFromRoute`, and
 * `selectStoryListSelectionFromRoute` in `storyListSelectors.ts` (compose-app).
 */

import { useRef, useMemo } from 'react';

import { IStoryListMessages, IStoryListSelection, ITabOptions } from '../../../../types';

import {
  deriveStoryListRoute,
  getStoryListSelectionFromRoute,
  isStoryListPath,
  parseStoryListFolderIdParam
} from './storyListRouteUtils';

export function useStoryListRoute(messages: IStoryListMessages): {
  tab: ITabOptions;
  currentFolderId: string | null;
  selectedStoryList: IStoryListSelection;
} {
  const pathname = window.location.pathname;
  const folderIdParam = parseStoryListFolderIdParam(pathname);

  // When the router transitions away from a StoryList path (e.g. to /stories/settings/...),
  // window.location.pathname updates before the route component unmounts. Deriving tab from
  // an unrecognised path causes a visible tab flash. Freeze the last known StoryList-derived
  // values until the component actually unmounts.
  const lastRouteRef = useRef<{ tab: ITabOptions; currentFolderId: string | null } | null>(null);

  const { tab, currentFolderId } = useMemo(() => {
    if (isStoryListPath(pathname)) {
      const derived = deriveStoryListRoute(pathname, folderIdParam);
      lastRouteRef.current = derived;
      return derived;
    }
    return lastRouteRef.current ?? deriveStoryListRoute(pathname, folderIdParam);
  }, [pathname, folderIdParam]);

  const selectedStoryList = useMemo(
    () => getStoryListSelectionFromRoute(tab, currentFolderId, messages),
    [tab, currentFolderId, messages]
  );

  return { tab, currentFolderId, selectedStoryList };
}
